import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BYPASS_LOG } from '@core/interceptors/loading-screen.interceptor';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'environments/environment';
import { DomainService } from './domain.service';
import { LoginRequestModel } from './models/login.model';
import { ClaimModel } from './models/claim.model';
import ClaimTypes from './enums/claimType.enum';
import { EmployeeService } from './corporate/employee.service';
import { EmployeeModel } from './models/employee.model';
import { UserType } from './enums/userType.enum';
import { map } from 'rxjs/operators';
import { ModuleActionModel } from './models/corporate/moduleAction.model';
import { pagesActions } from 'app/page-access';
import { StorageService } from './storage.service';
import { normalizeUrl } from 'utils/helps';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public currentUserSubject: BehaviorSubject<any>;
  public employeeSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;

  private readonly _KEY: string = 'currentRhUser';
  private readonly _ACTIONS: string = 'userActions';
  private readonly _EMPLOYEE: string = 'employee';

  normalizeUrl = normalizeUrl;

  constructor(
    private http: HttpClient,
    private employeeService: EmployeeService,
    private domainService: DomainService,
    private storageService: StorageService,
    private _router: Router
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem(this._KEY)));
    this.employeeSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem(this._EMPLOYEE))
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  public get employeeValue(): any {
    return this.employeeSubject.value;
  }

  public getToken(): string {
    return JSON.parse(localStorage.getItem(this._KEY))?.accessToken;
  }

  public GetName(): string {
    const jwtHelper = new JwtHelperService();
    const payload = jwtHelper.decodeToken(this.getToken());
    if (!payload) return undefined;
    var t = payload['user_name'];
    var FirstName = t
      ? t.split(' ')[0].charAt(0).toUpperCase() + t.split(' ')[0].substr(1).toLowerCase()
      : '';
    var SecondName = t
      ? t.split(' ')[1].charAt(0).toUpperCase() + t.split(' ')[1].substr(1).toLowerCase()
      : '';
    return FirstName + ' ' + SecondName;
  }

  public GetCompany(): string {
    const jwtHelper = new JwtHelperService();
    const payload = jwtHelper.decodeToken(this.getToken());
    if (!payload) return undefined;
    return payload['user_company'];
  }

  public getOrganization(): string {
    const jwtHelper = new JwtHelperService();
    const payload = jwtHelper.decodeToken(this.getToken());
    if (!payload) return undefined;
    return payload['organization_id'];
  }

  public getDomainName(): string {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.domainName);
    return claim?.value;
  }

  public getShortProfileImagePath(): string {
    return JSON.parse(localStorage.getItem(this._EMPLOYEE))?.shortProfileImagePath;
  }

  public getProfileImagePath(): string {
    return JSON.parse(localStorage.getItem(this._EMPLOYEE))?.profileImagePath;
  }

  public getUserId(): string {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.user_id);
    return claim?.value;
  }

  public getUserType(): string {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.user_type);
    return claim?.value;
  }

  public getAccessProfileId(): string {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.access_profile_id);
    return claim?.value;
  }

  public getUserLevels(): number[] {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.hierarchy_levels);
    if (!claim) return [];

    return (claim.value.split(',') || []).map((level: string) => parseInt(level));
  }

  private getClaims(): ClaimModel[] {
    const jwtHelper = new JwtHelperService();
    const payload = jwtHelper.decodeToken(this.getToken());
    if (payload) {
      const claims = Object.entries(payload).map(([key, value]) =>
        ClaimModel.create(key, `${value}`.toLowerCase())
      );
      return claims;
    } else return [];
  }

  public getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return headers;
  }

  login(params: LoginRequestModel): Observable<any> {
    return new Observable<any>(observer => {
      const domain = this.domainService.getDomain();
      const _headers = new HttpHeaders({
        'Content-Type': 'application/json',
        DomainName: domain.domainName,
      });
      this.http
        .post<any>(`${environment.apiUrl}/account/auth`, params, {
          headers: _headers,
        })
        .subscribe(
          user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem(this._KEY, JSON.stringify(user));

            //set employee if the user is a employee type
            this.setEmployeeWhenUserTypeIsEmployee();

            this.currentUserSubject.next(user);
            observer.next(user);
            observer.complete();
          },
          err => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  ssoLogin(token: string, employeeId: string, companyGroupId: string, bypassLoading: boolean = false): Observable<any> {
    return new Observable<any>(observer => {
      const domain = this.domainService.getDomain();
      const _headers = new HttpHeaders({
        'Content-Type': 'application/json',
        DomainName: domain.domainName,
      });
      this.http
        .post<any>(
          `${environment.apiUrl}/account/sso-exchange`,
          { otp: token, employeeId, companyGroupId },
          { headers: _headers, context: new HttpContext().set(BYPASS_LOG, bypassLoading) }
        )
        .subscribe(
          user => {
            localStorage.setItem(this._KEY, JSON.stringify(user));

            this.setEmployeeWhenUserTypeIsEmployee();

            this.currentUserSubject.next(user);
            observer.next(user);
            observer.complete();
          },
          err => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  setEmployeeWhenUserTypeIsEmployee() {
    if (this.getUserType() == 'employee') {
      this.getEmployeeByUserId();
    }
  }

  getEmployeeByUserId() {
    this.employeeService
      .get(this.getUserId())
      .subscribe(res => this.setEmployeeOnStorage(res.data));
  }

  setEmployeeOnStorage(employee: EmployeeModel) {
    const object = {
      id: employee.id,
      shortProfileImagePath: employee.shortProfileImagePath,
      profileImagePath: employee.profileImagePath,
    };

    localStorage.setItem(this._EMPLOYEE, JSON.stringify(object));
    this.employeeSubject.next(object);
  }

  removeEmployeeOnStorage() {
    localStorage.removeItem(this._EMPLOYEE);
    this.employeeSubject.next(null);
  }

  logout() {
    this.storageService.clearUserStorage();
    localStorage.removeItem(this._KEY);
    localStorage.removeItem(this._EMPLOYEE);
    localStorage.removeItem(this._ACTIONS);
    this.currentUserSubject.next(null);
    this.employeeSubject.next(null);
    const currentDomainName = this.domainService.getDomain()?.domainName;
    if (currentDomainName) {
      this.domainService
        .checkDomainName(currentDomainName, true)
        .subscribe(domain => {
          this.domainService.setDomain(domain);
        });
    }
  }

  public getUserRoles(): string[] {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.role);
    if (!claim) return undefined;

    return claim.value.split(',');
  }

  public getUserActions(): string[] {
    const claim = this.getClaims().find(f => f.type === ClaimTypes.action);
    if (!claim) return undefined;

    return claim.value.split(',');
  }

  loadAllowedActions(bypassLoading: boolean = false): Observable<any> {
    const profileId = this.getAccessProfileId();
    if (!profileId) return of(null);
    return this.http
      .get(`${environment.apiUrl}/AccessProfile/${profileId}/allowed-actions`, {
        context: new HttpContext().set(BYPASS_LOG, bypassLoading),
      })
      .pipe(
        map((res: any) => {
          const objects = res.map(pp => ModuleActionModel.create(pp));
          localStorage.setItem(this._ACTIONS, JSON.stringify(objects));
        })
      );
  }

  hasActionAllowed(action: string, url: string, onlySpecific: boolean = false): boolean {
    const raw = localStorage.getItem(this._ACTIONS);
    if (!raw) return false;

    const allowedActions = (JSON.parse(raw) as []).map(a =>
      ModuleActionModel.create(JSON.parse(a))
    );
    const cleanUrl = this.normalizeUrl(url);

    const hasSpecificPermission = allowedActions.some(
      a => a.action === action && a.moduleUrl === cleanUrl
    );

    const hasFullControl = allowedActions.some(a => a.isFullControl && a.moduleUrl === cleanUrl);

    return onlySpecific ? hasSpecificPermission : hasSpecificPermission || hasFullControl;
  }

  canAccessFeedbackSettings(url: string = '/corporate/employeesmanagement'): boolean {
    return this.hasActionAllowed(pagesActions.employeeManagements.canAccessFeedbackSettings, url);
  }

  public isLoggedIn(): boolean {
    const token = this.getToken();

    if (!token) {
      return false; // não tem token → não está logado
    }

    try {
      const jwtHelper = new JwtHelperService();
      return !jwtHelper.isTokenExpired(token);
    } catch (error) {
      return false; // se der erro ao validar token, considera não logado
    }
  }

  get payload(): any | undefined {
    const jwtHelper = new JwtHelperService();
    const payload = jwtHelper.decodeToken(this.getToken());

    if (!payload) return undefined;

    return payload;
  }

  get isOutsourcedLiableUser(): boolean {
    if (!this.payload) return false;

    return this.payload?.user_type === UserType.OutsourcedLiable.value;
  }

  get isAdmin(): boolean {
    return this.hasActionAllowed(pagesActions.default.fullControl, this._router.url);
  }

  get hasOrganization(): boolean {
    return (
      this.getOrganization() !== undefined &&
      this.getOrganization() !== 'undefined' &&
      this.getOrganization() !== '00000000000000000000000000000000'
    );
  }
}
